'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Zap, Star, Bug, Shield, TrendingUp, ChevronRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface ChangelogEntry {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  highlights: string[];
  changes: { type: 'new' | 'improved' | 'fixed' | 'security'; text: string }[];
}

// Latest version — used for badge tracking
export const LATEST_VERSION = '2.6.0';

const CHANGELOG: ChangelogEntry[] = [
  {
    version: '2.6.0',
    date: '2025-04-05',
    type: 'major',
    highlights: ['Dark Mode', 'Ricerca Globale', 'Sidebar Collassabile', 'Player Audio Waveform'],
    changes: [
      { type: 'new', text: 'Dark mode con toggle persistente in localStorage e variabili CSS' },
      { type: 'new', text: 'Ricerca globale (Ctrl+K) con ricerca pazienti, chiamate e pagine' },
      { type: 'new', text: 'Sidebar con navigazione raggruppata e sezioni collassabili' },
      { type: 'new', text: 'Player audio con waveform interattivo nelle registrazioni chiamate' },
      { type: 'new', text: 'Scorciatoie da tastiera: D=Dashboard, P=Pazienti, C=Calendario, N=Nuova chiamata' },
      { type: 'new', text: 'PWA manifest per installazione come app mobile' },
      { type: 'new', text: 'Sitemap.xml e robots.txt automatici' },
      { type: 'new', text: 'Open Graph meta tags su tutte le pagine pubbliche' },
      { type: 'new', text: 'Filtri persistenti in URL params (useUrlFilters hook)' },
      { type: 'new', text: 'Tour onboarding interattivo per nuovi utenti' },
      { type: 'new', text: 'Fuzzy matching pazienti duplicati con Fuse.js' },
      { type: 'new', text: 'Export PDF per report e fatture' },
      { type: 'new', text: 'Campagne email multi-canale via Brevo' },
      { type: 'new', text: 'WhatsApp template approvati via Twilio Content API' },
      { type: 'new', text: 'SMS delivery tracking real-time con status callback' },
      { type: 'new', text: 'Sync bidirezionale Cal.com (cancellazione e aggiornamento)' },
      { type: 'new', text: 'Reminder appuntamenti automatici via cron job' },
      { type: 'improved', text: 'Audit trail middleware su tutte le API CRUD' },
      { type: 'improved', text: 'Rate limiting potenziato per endpoint critici' },
      { type: 'improved', text: 'Skeleton loading uniformi su tutte le pagine' },
      { type: 'improved', text: 'Validazione CSV import con controllo duplicati e formato telefono' },
      { type: 'improved', text: 'Analytics charts collegate a dati aggregati reali dallo store' },
    ],
  },
  {
    version: '2.5.0',
    date: '2025-01-10',
    type: 'major',
    highlights: ['Portale Paziente Self-Service', 'Report Builder Avanzato', 'SLA Dashboard'],
    changes: [
      { type: 'new', text: 'Portale paziente con accesso via codice fiscale o OTP SMS' },
      { type: 'new', text: 'Report builder custom con selezione metriche e visualizzazioni' },
      { type: 'new', text: 'SLA Dashboard dedicata con trend e alert per operatore' },
      { type: 'new', text: 'Catalogo esami configurabile con codici, tempi e priorità' },
      { type: 'improved', text: 'Performance SSE migliorata del 40% con push registry ottimizzato' },
      { type: 'fixed', text: 'Corretto bug nel calcolo del tempo medio di gestione' },
    ],
  },
  {
    version: '2.4.0',
    date: '2024-12-20',
    type: 'minor',
    highlights: ['Import CSV Pazienti', 'Rilevamento Duplicati', 'Audit Trail Avanzato'],
    changes: [
      { type: 'new', text: 'Import CSV pazienti con drag-and-drop e anteprima 5 righe' },
      { type: 'new', text: 'Rilevamento automatico pazienti duplicati con merge guidato' },
      { type: 'new', text: 'Pagina audit trail dedicata con filtri avanzati e export CSV' },
      { type: 'improved', text: 'Fuzzy matching migliorato per la ricerca pazienti' },
      { type: 'fixed', text: 'Corretto problema con il retry SMS che non aggiornava lo stato' },
    ],
  },
  {
    version: '2.3.0',
    date: '2024-12-05',
    type: 'minor',
    highlights: ['SSE Real-time Push', 'Chat Operatori Migliorata', 'Twilio Hold/Transfer'],
    changes: [
      { type: 'new', text: 'SSE push registry: eventi real-time per chat, broadcast, annunci' },
      { type: 'new', text: 'Read receipts nella chat operatori (✓/✓✓)' },
      { type: 'new', text: 'Hold e trasferimento chiamate via Twilio REST API reale' },
      { type: 'improved', text: 'Unread badge count per canale nella chat operatori' },
      { type: 'security', text: 'Aggiornati header di sicurezza per le API routes' },
    ],
  },
  {
    version: '2.2.0',
    date: '2024-11-20',
    type: 'minor',
    highlights: ['Stripe Webhook Completo', 'Gestione Tenant Avanzata'],
    changes: [
      { type: 'new', text: 'Webhook Stripe completo per tutti gli eventi di abbonamento' },
      { type: 'new', text: 'CRUD completo tenant con gestione status e piano' },
      { type: 'improved', text: 'Analytics con caching 5 minuti per performance migliori' },
      { type: 'fixed', text: 'Corretto calcolo SLA operatori con dati reali' },
    ],
  },
];

const TYPE_CONFIG = {
  new: { label: 'Nuovo', color: 'bg-green-100 text-green-700', icon: Star },
  improved: { label: 'Migliorato', color: 'bg-blue-100 text-blue-700', icon: TrendingUp },
  fixed: { label: 'Corretto', color: 'bg-amber-100 text-amber-700', icon: Bug },
  security: { label: 'Sicurezza', color: 'bg-purple-100 text-purple-700', icon: Shield },
};

const VERSION_COLORS = {
  major: 'bg-primary text-white',
  minor: 'bg-blue-100 text-blue-700',
  patch: 'bg-muted text-muted-foreground',
};

export default function ChangelogPage() {
  const [expanded, setExpanded] = useState<Set<string>>(new Set([CHANGELOG[0].version]));

  const toggle = (v: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="bg-white dark:bg-card border-b border-border px-6 lg:px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Zap size={18} className="text-primary" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Changelog</h1>
              <p className="text-xs text-muted-foreground">Novità e aggiornamenti di LabCallManager</p>
            </div>
            <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${VERSION_COLORS.major}`}>
              v{CHANGELOG[0].version} — Ultima versione
            </span>
          </div>
        </div>

        <div className="px-6 lg:px-8 py-6 max-w-3xl">
          <div className="space-y-4">
            {CHANGELOG.map((entry) => (
              <div key={entry.version} className="bg-white dark:bg-card border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => toggle(entry.version)}
                  aria-expanded={expanded.has(entry.version)}
                  aria-controls={`changelog-${entry.version}`}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors text-left"
                >
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${VERSION_COLORS[entry.type]}`}>
                    v{entry.version}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {entry.highlights.map(h => (
                        <span key={h} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{h}</span>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(entry.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    className={`text-muted-foreground transition-transform ${expanded.has(entry.version) ? 'rotate-90' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {expanded.has(entry.version) && (
                  <div id={`changelog-${entry.version}`} className="px-5 pb-5 border-t border-border">
                    <div className="mt-4 space-y-2">
                      {entry.changes.map((change, i) => {
                        const cfg = TYPE_CONFIG[change.type];
                        const Icon = cfg.icon;
                        return (
                          <div key={i} className="flex items-start gap-3">
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 mt-0.5 ${cfg.color}`}>
                              <Icon size={10} aria-hidden="true" /> {cfg.label}
                            </span>
                            <p className="text-sm text-foreground">{change.text}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
